/**
 * Frontend JavaScript for Yum Posts Block.
 *
 * Handles load more functionality, filter bar, and carousel initialization.
 *
 * @package yum-posts-block
 */

( function () {
    'use strict';

    /**
     * Initialize load more functionality.
     */
    function initLoadMore() {
        const buttons = document.querySelectorAll(
            '.yum-posts-block__load-more'
        );

        buttons.forEach( ( button ) => {
            button.addEventListener( 'click', handleLoadMore );
        } );
    }

    /**
     * Initialize filter bar functionality.
     */
    function initFilterBar() {
        const filterBars = document.querySelectorAll(
            '.yum-posts-block__filter-bar'
        );

        filterBars.forEach( ( filterBar ) => {
            const wrapper = filterBar.closest( '.yum-posts-block' );
            if ( ! wrapper ) {
                return;
            }

            const grid = wrapper.querySelector( '.yum-posts-block__grid' );
            const buttons = filterBar.querySelectorAll( '.yum-posts-block__filter-button' );
            const attributes = JSON.parse( wrapper.dataset.attributes || '{}' );

            // Filter buttons click handler.
            buttons.forEach( ( button ) => {
                button.addEventListener( 'click', async () => {
                    // Update active state.
                    buttons.forEach( ( btn ) => btn.classList.remove( 'yum-posts-block__filter-button--active' ) );
                    button.classList.add( 'yum-posts-block__filter-button--active' );

                    const currentFilter = button.dataset.filter;
                    // Get search value from independent search field if present.
                    const searchInput = wrapper.querySelector( '.yum-posts-block__search-input' );
                    const currentSearch = searchInput ? searchInput.value.trim() : '';

                    await loadFilteredPosts( wrapper, grid, attributes, currentFilter, currentSearch );
                } );
            } );
        } );
    }

    /**
     * Initialize independent search functionality.
     */
    function initSearch() {
        const searchFields = document.querySelectorAll(
            '.yum-posts-block__search'
        );

        searchFields.forEach( ( searchContainer ) => {
            const wrapper = searchContainer.closest( '.yum-posts-block' );
            if ( ! wrapper ) {
                return;
            }

            const grid = wrapper.querySelector( '.yum-posts-block__grid' );
            const searchInput = searchContainer.querySelector( '.yum-posts-block__search-input' );
            const attributes = JSON.parse( wrapper.dataset.attributes || '{}' );

            if ( ! searchInput ) {
                return;
            }

            let debounceTimer;
            searchInput.addEventListener( 'input', () => {
                clearTimeout( debounceTimer );
                debounceTimer = setTimeout( async () => {
                    // Get current filter from filter bar if present.
                    const filterBar = wrapper.querySelector( '.yum-posts-block__filter-bar' );
                    let currentFilter = '*';
                    if ( filterBar ) {
                        const activeButton = filterBar.querySelector( '.yum-posts-block__filter-button--active' );
                        currentFilter = activeButton ? activeButton.dataset.filter : '*';
                    }
                    const currentSearch = searchInput.value.trim();

                    await loadFilteredPosts( wrapper, grid, attributes, currentFilter, currentSearch );
                }, 300 );
            } );
        } );
    }

    /**
     * Load filtered posts via AJAX.
     *
     * @param {HTMLElement} wrapper    Block wrapper.
     * @param {HTMLElement} grid       Grid container.
     * @param {Object}      attributes Block attributes.
     * @param {string}      filter     Category slug or '*' for all.
     * @param {string}      search     Search term.
     */
    async function loadFilteredPosts( wrapper, grid, attributes, filter, search ) {
        if ( ! grid || ! window.yumPostsBlock ) {
            return;
        }

        // Add loading state.
        wrapper.classList.add( 'is-filtering' );
        grid.style.opacity = '0.5';

        try {
            const response = await fetch(
                `${ window.yumPostsBlock.restUrl }filter`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.yumPostsBlock.nonce,
                    },
                    body: JSON.stringify( {
                        attributes,
                        filter,
                        search,
                    } ),
                }
            );

            if ( ! response.ok ) {
                throw new Error( 'Network response was not ok' );
            }

            const data = await response.json();

            // Replace grid content.
            grid.innerHTML = data.html || '';

            // Re-equalize overlay heights after content change.
            equalizeContentHeights();

            // Update pagination data.
            wrapper.dataset.page = '1';
            wrapper.dataset.maxPages = data.maxPages;

            // Store current filter in wrapper for pagination.
            wrapper.dataset.currentFilter = filter;
            wrapper.dataset.currentSearch = search;

            // Show/hide load more button.
            const loadMoreBtn = wrapper.querySelector( '.yum-posts-block__load-more' );
            if ( loadMoreBtn ) {
                const loadMoreWrapper = loadMoreBtn.closest( '.yum-posts-block__load-more-wrapper' );
                if ( data.maxPages > 1 ) {
                    loadMoreBtn.style.display = '';
                    if ( loadMoreWrapper ) {
                        loadMoreWrapper.style.display = '';
                    }
                } else {
                    loadMoreBtn.style.display = 'none';
                    if ( loadMoreWrapper ) {
                        loadMoreWrapper.style.display = 'none';
                    }
                }
            }

            // Update classic pagination.
            const pagination = wrapper.querySelector( '.yum-posts-block__pagination' );
            if ( pagination ) {
                updatePagination( wrapper, pagination, 1, data.maxPages, filter, search );
            }
        } catch ( error ) {
            console.error( 'Error filtering posts:', error );
        } finally {
            // Remove loading state.
            wrapper.classList.remove( 'is-filtering' );
            grid.style.opacity = '';
        }
    }

    /**
     * Update classic pagination after filtering.
     *
     * @param {HTMLElement} wrapper    Block wrapper.
     * @param {HTMLElement} pagination Pagination container.
     * @param {number}      current    Current page.
     * @param {number}      maxPages   Total pages.
     * @param {string}      filter     Current filter.
     * @param {string}      search     Current search.
     */
    function updatePagination( wrapper, pagination, current, maxPages, filter, search ) {
        if ( maxPages <= 1 ) {
            pagination.style.display = 'none';
            return;
        }

        pagination.style.display = '';

        // Build new pagination HTML.
        let html = '';
        const range = 2;
        const start = Math.max( 1, current - range );
        const end = Math.min( maxPages, current + range );

        // Previous button.
        if ( current > 1 ) {
            html += `<button type="button" class="yum-posts-block__pagination-item yum-posts-block__pagination-prev" data-page="${ current - 1 }">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>`;
        } else {
            html += `<span class="yum-posts-block__pagination-item yum-posts-block__pagination-prev disabled">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </span>`;
        }

        // First page + ellipsis.
        if ( start > 1 ) {
            html += `<button type="button" class="yum-posts-block__pagination-item" data-page="1">1</button>`;
            if ( start > 2 ) {
                html += `<span class="yum-posts-block__pagination-item yum-posts-block__pagination-ellipsis">...</span>`;
            }
        }

        // Page numbers.
        for ( let i = start; i <= end; i++ ) {
            if ( i === current ) {
                html += `<span class="yum-posts-block__pagination-item active" aria-current="page">${ i }</span>`;
            } else {
                html += `<button type="button" class="yum-posts-block__pagination-item" data-page="${ i }">${ i }</button>`;
            }
        }

        // Last page + ellipsis.
        if ( end < maxPages ) {
            if ( end < maxPages - 1 ) {
                html += `<span class="yum-posts-block__pagination-item yum-posts-block__pagination-ellipsis">...</span>`;
            }
            html += `<button type="button" class="yum-posts-block__pagination-item" data-page="${ maxPages }">${ maxPages }</button>`;
        }

        // Next button.
        if ( current < maxPages ) {
            html += `<button type="button" class="yum-posts-block__pagination-item yum-posts-block__pagination-next" data-page="${ current + 1 }">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            </button>`;
        } else {
            html += `<span class="yum-posts-block__pagination-item yum-posts-block__pagination-next disabled">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
            </span>`;
        }

        pagination.innerHTML = html;

        // Add click handlers to new pagination buttons.
        pagination.querySelectorAll( 'button[data-page]' ).forEach( ( btn ) => {
            btn.addEventListener( 'click', () => {
                const page = parseInt( btn.dataset.page, 10 );
                loadFilteredPage( wrapper, page, filter, search );
            } );
        } );
    }

    /**
     * Load a specific page with current filter.
     *
     * @param {HTMLElement} wrapper Block wrapper.
     * @param {number}      page    Page number to load.
     * @param {string}      filter  Current filter.
     * @param {string}      search  Current search.
     */
    async function loadFilteredPage( wrapper, page, filter, search ) {
        const grid = wrapper.querySelector( '.yum-posts-block__grid' );
        const pagination = wrapper.querySelector( '.yum-posts-block__pagination' );
        const attributes = JSON.parse( wrapper.dataset.attributes || '{}' );

        if ( ! grid || ! window.yumPostsBlock ) {
            return;
        }

        // Add loading state.
        wrapper.classList.add( 'is-filtering' );
        grid.style.opacity = '0.5';

        try {
            const response = await fetch(
                `${ window.yumPostsBlock.restUrl }filter`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.yumPostsBlock.nonce,
                    },
                    body: JSON.stringify( {
                        attributes,
                        filter,
                        search,
                        page,
                    } ),
                }
            );

            if ( ! response.ok ) {
                throw new Error( 'Network response was not ok' );
            }

            const data = await response.json();

            // Replace grid content.
            grid.innerHTML = data.html || '';

            // Re-equalize overlay heights after content change.
            equalizeContentHeights();

            // Update pagination data.
            wrapper.dataset.page = page.toString();
            wrapper.dataset.maxPages = data.maxPages;

            // Update pagination UI.
            if ( pagination ) {
                updatePagination( wrapper, pagination, page, data.maxPages, filter, search );
            }

            // Scroll to top of block.
            wrapper.scrollIntoView( { behavior: 'smooth', block: 'start' } );
        } catch ( error ) {
            console.error( 'Error loading page:', error );
        } finally {
            // Remove loading state.
            wrapper.classList.remove( 'is-filtering' );
            grid.style.opacity = '';
        }
    }

    /**
     * Handle load more button click.
     *
     * @param {Event} event Click event.
     */
    async function handleLoadMore( event ) {
        const button = event.currentTarget;
        const wrapper = button.closest( '.yum-posts-block' );
        const grid = wrapper.querySelector( '.yum-posts-block__grid' );

        if ( ! wrapper || ! grid || button.disabled ) {
            return;
        }

        // Get current state.
        const currentPage = parseInt( wrapper.dataset.page, 10 ) || 1;
        const maxPages = parseInt( wrapper.dataset.maxPages, 10 ) || 1;
        const attributes = JSON.parse( wrapper.dataset.attributes || '{}' );

        // Get current filter from filter bar if present.
        let currentFilter = wrapper.dataset.currentFilter || '*';
        const filterBar = wrapper.querySelector( '.yum-posts-block__filter-bar' );
        if ( filterBar ) {
            const activeButton = filterBar.querySelector( '.yum-posts-block__filter-button--active' );
            if ( activeButton ) {
                currentFilter = activeButton.dataset.filter || '*';
            }
        }

        // Get current search value.
        let currentSearch = wrapper.dataset.currentSearch || '';
        const searchInput = wrapper.querySelector( '.yum-posts-block__search-input' );
        if ( searchInput ) {
            currentSearch = searchInput.value.trim();
        }

        // Check if we can load more.
        if ( currentPage >= maxPages ) {
            button.style.display = 'none';
            return;
        }

        // Set loading state.
        button.classList.add( 'is-loading' );
        button.disabled = true;

        try {
            const response = await fetch(
                `${ window.yumPostsBlock.restUrl }posts`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': window.yumPostsBlock.nonce,
                    },
                    body: JSON.stringify( {
                        attributes,
                        page: currentPage + 1,
                        filter: currentFilter,
                        search: currentSearch,
                    } ),
                }
            );

            if ( ! response.ok ) {
                throw new Error( 'Network response was not ok' );
            }

            const data = await response.json();

            if ( data.html ) {
                // Append new posts.
                grid.insertAdjacentHTML( 'beforeend', data.html );

                // Re-equalize overlay heights after new items.
                equalizeContentHeights();

                // Update page counter.
                wrapper.dataset.page = currentPage + 1;

                // Hide button if no more posts.
                if ( ! data.hasMore ) {
                    button.style.display = 'none';
                }
            }
        } catch ( error ) {
            console.error( 'Error loading more posts:', error );
        } finally {
            // Remove loading state.
            button.classList.remove( 'is-loading' );
            button.disabled = false;
        }
    }

    /**
     * Equalize content heights per row so all cards have uniform content areas.
     */
    function equalizeContentHeights() {
        const grids = document.querySelectorAll( '.yum-posts-block__grid' );

        grids.forEach( ( grid ) => {
            const items = grid.querySelectorAll( '.yum-posts-block__item' );
            if ( items.length < 2 ) {
                return;
            }

            // Reset heights first.
            items.forEach( ( item ) => {
                const content = item.querySelector( '.yum-posts-block__content' );
                if ( content ) {
                    content.style.minHeight = '';
                }
            } );

            // Group items by row (same offsetTop).
            const rows = {};
            items.forEach( ( item ) => {
                const top = item.offsetTop;
                if ( ! rows[ top ] ) {
                    rows[ top ] = [];
                }
                rows[ top ].push( item );
            } );

            // Equalize content heights per row.
            Object.values( rows ).forEach( ( rowItems ) => {
                if ( rowItems.length < 2 ) {
                    return;
                }
                let maxHeight = 0;
                const contents = [];
                rowItems.forEach( ( item ) => {
                    const content = item.querySelector( '.yum-posts-block__content' );
                    if ( content ) {
                        contents.push( content );
                        maxHeight = Math.max( maxHeight, content.offsetHeight );
                    }
                } );
                contents.forEach( ( content ) => {
                    content.style.minHeight = maxHeight + 'px';
                } );
            } );
        } );
    }

    /**
     * Initialize all functionality.
     */
    function init() {
        initLoadMore();
        initFilterBar();
        initSearch();
        equalizeContentHeights();
        window.addEventListener( 'resize', equalizeContentHeights );
    }

    /**
     * Initialize when DOM is ready.
     */
    if ( document.readyState === 'loading' ) {
        document.addEventListener( 'DOMContentLoaded', init );
    } else {
        init();
    }
} )();
